import { selectedChannelsForTracking } from '~publish/legacy/composer/composer/editor/ComposerEditor/hooks/useSelectedChannelsForTracking'
import { selectCurrentOrganizationId } from '~publish/legacy/organizations/selectors'
import store from '~publish/legacy/store'

import type { AutocompleteItem } from '../../autocomplete/index'

const isAutocompleteItem = (value: unknown): value is AutocompleteItem => {
  if (typeof value !== 'object' || value === null) return false
  if (
    !('key' in value) ||
    typeof value.key !== 'string' ||
    !('text' in value) ||
    typeof value.text !== 'string' ||
    !('data' in value) ||
    typeof value.data !== 'object' ||
    value.data === null
  ) {
    return false
  }

  return true
}

export function getLocalStorageKey(): string {
  const orgId = selectCurrentOrganizationId(store.getState()) ?? ''

  return `linkedin-annotation-selected-${orgId}`
}

export function getRecentItems(): AutocompleteItem[] | null {
  const recentItems = localStorage.getItem(getLocalStorageKey())

  if (recentItems) {
    try {
      const parsedItems: unknown = JSON.parse(recentItems)
      if (!Array.isArray(parsedItems)) return null

      return parsedItems.filter(isAutocompleteItem).map((item) =>
        Object.assign({}, item, {
          data: {
            ...item.data,
            recentItem: true,
          },
        }),
      )
    } catch {
      return null
    }
  }

  return null
}

export function removeRecentItem(itemKey: string): boolean {
  const localStorageKey = getLocalStorageKey()
  const recentItems = localStorage.getItem(localStorageKey)
  if (recentItems) {
    try {
      const parsedItems: unknown = JSON.parse(recentItems)
      if (!Array.isArray(parsedItems)) return false

      const updatedItems = parsedItems.filter(
        (item): item is AutocompleteItem =>
          isAutocompleteItem(item) && item.key !== itemKey,
      )

      localStorage.setItem(
        localStorageKey,
        JSON.stringify(updatedItems.slice(0, 3)),
      )

      return true
    } catch {
      return false
    }
  }

  return false
}

export function isRecentItem(itemKey: string): boolean {
  const recentItems = getRecentItems()
  return Boolean(recentItems?.some((item) => item.key === itemKey))
}

export function onSelectItem(item: AutocompleteItem): void {
  const recentItems = getRecentItems() || []
  const items = [item, ...recentItems]
  const deduplicated = new Map(
    items.map((autocompleteItem) => [autocompleteItem.key, autocompleteItem]),
  )

  const trackingDetails = selectedChannelsForTracking({
    serviceName: 'linkedin',
  })

  if (item.data.recentItem) {
    trackingDetails.sendMentionRecentSuggestionAccepted()
  } else {
    trackingDetails.sendMentionSuggestionAccepted()
  }

  localStorage.setItem(
    getLocalStorageKey(),
    JSON.stringify([...deduplicated.values()].slice(0, 3)),
  )
}
